import { z } from 'zod';

// Checklists (/api/checklists). Response types: `X` is the JSON on the wire (dates as ISO
// strings), `XOut` what the service returns before serialisation (dates as Date).

const Timestamp = z.codec(z.iso.datetime({ offset: true }), z.date(), {
  decode: (s) => new Date(s),
  encode: (d) => d.toISOString(),
});

export const ChecklistOwner = z.enum([
  'program_manager',
  'npi',
  'supervisor',
  'speaker',
  'research_services',
  'platform',
]);
export type ChecklistOwner = z.infer<typeof ChecklistOwner>;

export const ChecklistState = z.enum(['blocked', 'open', 'done', 'skipped', 'not_applicable']);
export type ChecklistState = z.infer<typeof ChecklistState>;

export const ChecklistCompletion = z.enum(['manual', 'automatic']);
export type ChecklistCompletion = z.infer<typeof ChecklistCompletion>;

export const ChecklistCompletedVia = z.enum(['operator', 'automatic', 'import']);
export type ChecklistCompletedVia = z.infer<typeof ChecklistCompletedVia>;

export const ChecklistNoQuery = z.strictObject({});

export const ChecklistListQuery = z.strictObject({ subjectType: z.string().min(1), subjectId: z.string().min(1) });
export type ChecklistListQuery = z.input<typeof ChecklistListQuery>;

export const ChecklistOverdueQuery = z.strictObject({ owner: z.union([z.literal(''), ChecklistOwner]).optional() });
export type ChecklistOverdueQuery = z.input<typeof ChecklistOverdueQuery>;

export const ChecklistItemParams = z.strictObject({ id: z.string().regex(/^[0-9a-f-]{36}$/i, 'Not an id') });

/** Evidence fields are the template's own (checked by its beforeComplete hook). */
export const ChecklistCompleteRequest = z.strictObject({
  note: z.string().nullable().optional(),
  evidence: z.record(z.string(), z.unknown()).nullable().optional(),
});
export type ChecklistCompleteRequest = z.input<typeof ChecklistCompleteRequest>;

/** Skip and not applicable. */
export const ChecklistReasonRequest = z.strictObject({ reason: z.string().trim().min(1, 'A reason is required') });
export type ChecklistReasonRequest = z.input<typeof ChecklistReasonRequest>;

export const ChecklistReopenRequest = z.strictObject({});
export type ChecklistReopenRequest = z.input<typeof ChecklistReopenRequest>;

export const ChecklistItem = z.object({
  id: z.string(),
  templateKey: z.string(),
  templateVersion: z.number(),
  subjectType: z.string(),
  subjectId: z.string(),
  stepKey: z.string(),
  label: z.string(),
  owner: z.string(),
  completion: ChecklistCompletion,
  position: z.number(),
  state: ChecklistState,
  dueAt: Timestamp.nullable(),
  completedAt: Timestamp.nullable(),
  completedBy: z.string().nullable(),
  completedVia: ChecklistCompletedVia.nullable(),
  evidence: z.record(z.string(), z.unknown()),
  note: z.string().nullable(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
  overdue: z.boolean(),
});
export type ChecklistItem = z.input<typeof ChecklistItem>;
export type ChecklistItemOut = z.output<typeof ChecklistItem>;

export const ChecklistGroup = z.object({
  templateKey: z.string(),
  label: z.string(),
  items: z.array(ChecklistItem),
  progress: z.object({ total: z.number(), finished: z.number() }),
  overdue: z.number(),
});
export type ChecklistGroup = z.input<typeof ChecklistGroup>;
export type ChecklistGroupOut = z.output<typeof ChecklistGroup>;

export const ChecklistTemplateSummary = z.object({
  key: z.string(),
  version: z.number(),
  label: z.string(),
  subjectType: z.string(),
  steps: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      owner: ChecklistOwner,
      completion: ChecklistCompletion,
      dependsOn: z.array(z.string()),
      due: z.object({ from: z.string(), days: z.number() }).nullable(),
    }),
  ),
});
export type ChecklistTemplateSummary = z.infer<typeof ChecklistTemplateSummary>;
